import React, { useState, useRef, useEffect } from "react";
import { Box, Button, Tooltip, useToast } from "@chakra-ui/react";
import { isImpersonating } from "../../utils/impersonation";
import { beginActivity, endActivity } from "../../utils/activityTracker";
import { jsPDF } from "jspdf";
import { ExternalRecordingPage } from "./components/ExternalRecordingPage";
import { cortiIntegrationService } from "./services/cortiIntegrationService";
import { withAIQueryTracking, trackUserEvent } from "../../utils/userEventTracker";
import {
  supabaseUrl,
  bucketName,
  bucketName_files,
  tableName,
  prompts,
  getBackendURL,
} from "../../components/env";
import {
  extractTranscriptPlainTextForClinicalAi,
  generateClinicalTabsAndPersistCopilotSnapshot,
} from "./utils/copilotBackupUtils";
import { runLiveAiAnalysis } from "./utils/liveTranscriptAiAnalysis";
import { tableForPatientId } from "../medstudent/chartSource";
import { supabaseSchema } from "../../components/supabaseClient";
import { getSupabase, supabasePublic } from "../../components/supabaseClient";
import { checkCreditLimitAndUpdateUI } from "../../utils/creditLimitChecker";
import { getLabel } from "../../config/roles";
import { getUserSchema } from "./utils/databaseUtils";
// Add microphone icon import
import { FaMicrophone } from "react-icons/fa";
import { ExternalRecordingService } from "./services/externalRecordingService";
import {
  recoverFromBackup as recoverTranscriptionFromBackup,
  getBackupStatus,
  getAnyTranscriptionBackup,
  searchTranscriptionBackup,
} from "./utils/transcriptionBackupUtils";
import { useWindowMessaging } from "./hooks/useWindowMessaging";

const AudioRecorder = ({
  state,
  refId,
  onDataChange,
  session,
  popDeps,
  setSession,
  override,
  setShowPersistentComponent,
  condFunc,
  autoSaveSetting = true,
  isTranscriptionModalOpen,
  setIsTranscriptionModalOpen,
  transcriptionData,
  setTranscriptionData,
  isMinimized = false,
  userRole,
  /** Narrow column (visit page, edit-doc toolbar): drop min-height trap; main button stretches full width */
  compactEmbed = false,
}) => {
  // Mark that AudioRecorder is mounted so other entry points (RecordingBubble)
  // don't duplicate AI-on-save work.
  React.useEffect(() => {
    window.__notesAiAudioRecorderMounted = true;
    return () => {
      window.__notesAiAudioRecorderMounted = false;
    };
  }, []);

  const [isExternalWindowOpen, setIsExternalWindowOpen] = useState(false);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [audioSaved, setAudioSaved] = useState(null);
  const [aiRecapEnabled, setAiRecapEnabled] = useState(true);
  const [sharedDisplayInfo, setSharedDisplayInfo] = useState(null); // Track what's being shared for system audio
  const externalWindowRef = useRef(null);
  /** Window reference when the popup was opened via RecordingBubble (not ExternalRecordingPage ref). */
  const externalPopupMessageTargetRef = useRef(null);
  const handleAudioSavedRef = useRef(async () => {});
  const shouldOpenWindowRef = useRef(false);
  const transcriptSessionMap = useRef({}); // sessionId → transcriptId, for linking after audio upload
  const savedTranscriptSessions = useRef(new Set()); // sessionIds that have already been saved, to prevent duplicates
  const toast = useToast();

  const { handleExternalAudioComplete } = useWindowMessaging({
    onAudioSaved: (result) => handleAudioSavedRef.current(result),
    bucketName: bucketName || bucketName_files || "files",
    tableName,
    supabaseUrl,
    state,
  });

  React.useEffect(() => {
    const onMessage = (event) => {
      const t = event?.data?.type;
      if (typeof t === "string" && (t.startsWith("REQUEST_") || t.startsWith("PARENT_"))) {
        console.log("🎤 [AudioRecorder] Root message listener received event:", {
          type: t,
          data: event.data,
          origin: event.origin,
          sourceExists: !!event.source,
        });
      }
      if (
        typeof t === "string" &&
        event.source &&
        event.source !== window &&
        typeof event.source.postMessage === "function"
      ) {
        if (
          /^(RECORDING_|AUDIO_|REQUEST_|SAVE_LIVE|LIVE_|EXPORT_|TRANSCRIPTION_|PARENT_)/.test(
            t,
          )
        ) {
          externalPopupMessageTargetRef.current = event.source;
        }
      }
      return handleExternalAudioComplete(event);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [handleExternalAudioComplete]);

  const postMessageToExternalRecordingWindow = React.useCallback((payload) => {
    const win = externalPopupMessageTargetRef.current;
    if (
      win &&
      typeof win.closed === "boolean" &&
      !win.closed &&
      typeof win.postMessage === "function"
    ) {
      try {
        win.postMessage(payload, "*");
        return true;
      } catch (e) {
        console.warn(
          "[AudioRecorder] postMessage to external popup failed:",
          e?.message || e,
        );
      }
    }
    if (externalWindowRef.current?.sendMessage) {
      externalWindowRef.current.sendMessage(payload);
      return true;
    }
    return false;
  }, []);

  // Recording state management with persistence
  const [recordingState, setRecordingState] = useState(() => {
    // Load state from localStorage or default to 'closed'
    try {
      const saved = localStorage.getItem("audioRecorderRecordingState");
      return saved || "closed";
    } catch {
      return "closed";
    }
  });

  const [liveTranscriptionDefault] = useState(() => {
    try {
      const stored = localStorage.getItem(
        "external_live_transcription_enabled",
      );
      return stored !== null ? JSON.parse(stored) : true;
    } catch (_) {
      return true;
    }
  });

  const [liveFactExtractionDefault] = useState(() => {
    try {
      const stored = localStorage.getItem(
        "external_live_fact_extraction_enabled",
      );
      return stored !== null ? JSON.parse(stored) : true;
    } catch (_) {
      return true;
    }
  });

  // PDF export related state
  const [isTranscribeModalOpen, setIsTranscribeModalOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [facts, setFacts] = useState([]);
  const [soapNotes, setSoapNotes] = useState(null);
  const [lastElapsedTime, setLastElapsedTime] = useState(0);

  // Transcription state management
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptionProgress, setTranscriptionProgress] = useState(null);
  const [transcriptionResults, setTranscriptionResults] = useState(null);
  const [transcriptionError, setTranscriptionError] = useState(null);
  const [showResults, setShowResults] = useState(false);

  // Add event listener for PDF export requests from external window
  React.useEffect(() => {
    const handleExportToPDF = (event) => {
      console.log("📄 Received PDF export request:", event.detail);

      // Create patient context from the recording data
      const patientContext = {
        ...state,
        id: event.detail.patientId,
        [prompts[0][0]]: event.detail.patientName,
      };

      // Set patient context and duration for PDF generation
      setSelectedPatient(patientContext);
      setLastElapsedTime(event.detail.duration || 0);

      // Use real transcription data if available, otherwise generate PDF with sample data
      if (transcriptionResults && transcriptionResults.transcript) {
        setTimeout(() => {
          generatePDFWithRealData();
        }, 100);
      } else {
        setTimeout(() => {
          generateDirectPDF();
        }, 100);
      }
    };

    window.addEventListener("exportToPDF", handleExportToPDF);
    return () => {
      window.removeEventListener("exportToPDF", handleExportToPDF);
    };
  }, [state, transcriptionResults]);

  // Add event listener for transcription requests from external window
  React.useEffect(() => {
    const handleRequestTranscription = async (event) => {
      console.log(
        "🎤 Received transcription request from external window:",
        event.detail,
      );

      // Use audioUrl from the request, or fall back to saved audioUrl
      const audioUrl =
        event.detail.audioUrl ||
        audioSaved?.audioUrl ||
        audioSaved?.audioData?.audioUrl;
      if (!audioUrl) {
        console.warn("⚠️ No audio URL in transcription request");
        toast({
          title: "Transcription Failed",
          description:
            "No audio URL available. Please try saving the recording again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // Extract audio metadata for the transcription record (prefer explicit request detail from external window)
      const audioMetadata = {
        audio_duration:
          event.detail?.duration ?? audioSaved?.audioData?.duration ?? 0,
        audio_file_name:
          event.detail?.audioFileName ||
          audioSaved?.audioData?.name ||
          audioSaved?.audioData?.displayName ||
          "Recording",
        audio_source:
          event.detail?.audioSource ||
          audioSaved?.audioData?.source ||
          "recording",
        audio_url:
          event.detail?.audio_url ||
          event.detail?.audioUrl ||
          audioUrl ||
          "",
      };

      const src = event.detail?.sourceWindow;
      if (
        src &&
        typeof src.postMessage === "function" &&
        (typeof src.closed !== "boolean" || !src.closed)
      ) {
        externalPopupMessageTargetRef.current = src;
      }

      // Start transcription
      await startTranscription(audioUrl, state, audioMetadata);
    };

    window.addEventListener("requestTranscription", handleRequestTranscription);
    return () => {
      window.removeEventListener(
        "requestTranscription",
        handleRequestTranscription,
      );
    };
  }, [state, audioSaved]);

  // Handle saving live transcript from external window
  React.useEffect(() => {
    const handleSaveLiveTranscript = async (event) => {
      console.log("🎤 Received save live transcript request:", event.detail);

      const { transcriptText, audioUrl, duration, sessionId, isAutoSave, patientId: eventPatientId, patientName: eventPatientName } =
        event.detail;

      // Prevent duplicate saves for the same recording session
      if (sessionId && savedTranscriptSessions.current.has(sessionId)) {
        console.log(
          "🎤 Transcript already saved for session",
          sessionId,
          "- skipping duplicate",
        );
        // Notify the external window so it can hide the spinner/prompt
        postMessageToExternalRecordingWindow({
          type: "LIVE_TRANSCRIPT_SAVED",
          success: true,
        });
        return;
      }

      // For manual saves (user clicking "Save Live Transcript"), require non-empty text.
      // For auto-saves triggered by AUDIO_RECORDING_COMPLETE, always proceed so every
      // audio save gets a corresponding transcript entry (even with empty text), making
      // it impossible for audio to be saved without a transcript record.
      if (!isAutoSave && (!transcriptText || !transcriptText.trim())) {
        toast({
          title: "No Transcript",
          description: "No live transcript text available to save.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Mark this session as saved before the async work to prevent race-condition duplicates
      if (sessionId) {
        savedTranscriptSessions.current.add(sessionId);
      }

      try {
        const supabase = await getSupabase();
        // Use patientId from event if provided (from direct upload), otherwise fall back to component state
        const patientId = eventPatientId || state?.id || refId || state?.scheduleId;

        const { data: existingData, error: fetchError } = await supabase
          .from(tableForPatientId(patientId))
          .select("transcripts")
          .eq("id", patientId)
          .maybeSingle();

        if (fetchError) throw fetchError;

        const existingList = Array.isArray(existingData?.transcripts)
          ? existingData.transcripts
          : existingData?.transcripts
            ? [existingData.transcripts]
            : [];

        // The popup (direct path) or useWindowMessaging may already have
        // appended this session's live transcript; the in-memory set above
        // does not survive a remount, so also dedupe against the row itself.
        const alreadyInRow =
          sessionId &&
          existingList.some(
            (t) => t?.is_live_transcript && t?.session_id === sessionId,
          );
        if (alreadyInRow) {
          console.log(
            "🎤 Live transcript for session already in patient row - skipping duplicate",
            sessionId,
          );
          postMessageToExternalRecordingWindow({
            type: "LIVE_TRANSCRIPT_SAVED",
            success: true,
          });
          window.dispatchEvent(new CustomEvent("transcriptionSaved"));
          return;
        }

        const uniqueId =
          typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : Date.now().toString();

        // If sessionId is present this is an early save (audio not uploaded yet).
        // Store the mapping so linkLiveTranscriptToAudio can patch the URL later.
        // Don't fall back to audioSaved here — that might be a previous recording.
        if (sessionId) {
          transcriptSessionMap.current[sessionId] = uniqueId;
        }

        // When the audio URL is already known (e.g. direct upload / AUDIO_SAVED_SUCCESS),
        // persist it so AudioPlayerModal can match this transcript to the row (it matches
        // by audio_url or audio_file_name). When sessionId is set but url is not ready yet,
        // leave both empty; linkLiveTranscriptToAudio patches them after parent upload.
        const resolvedAudioUrl =
          typeof audioUrl === "string" && audioUrl.trim() ? audioUrl.trim() : "";
        const fromUrlFileName = resolvedAudioUrl
          ? resolvedAudioUrl.split("/").pop()?.split("?")[0] || ""
          : "";
        const deferredUntilLinked = !!(sessionId && !resolvedAudioUrl);

        const liveTranscriptEntry = {
          id: uniqueId,
          session_id: sessionId || null,
          patient_id: patientId?.toString() || "",
          name:
            state?.[prompts[0][0]] || state?.patient_name || "Unknown Patient",
          transcript: transcriptText?.trim() ? [transcriptText.trim()] : [],
          facts: [],
          soap_notes: {},
          audio_duration: duration || audioSaved?.audioData?.duration || 0,
          audio_source: "live_transcript",
          audio_file_name: deferredUntilLinked
            ? ""
            : fromUrlFileName ||
              audioSaved?.audioData?.name ||
              audioSaved?.audioData?.displayName ||
              "Recording",
          audio_url: deferredUntilLinked
            ? ""
            : resolvedAudioUrl || audioSaved?.audioUrl || "",
          estimated_credit_usage: null,
          created_at: new Date().toISOString(),
          is_live_transcript: true,
        };

        const newTranscripts = [...existingList, liveTranscriptEntry];

        const { error: updateError } = await supabase
          .from(tableForPatientId(patientId))
          .update({ transcripts: newTranscripts })
          .eq("id", patientId);

        if (updateError) throw updateError;

        trackUserEvent('transcript_complete', {
          metadata: {
            source: 'live_transcript',
            transcript_segments: transcriptText?.trim() ? 1 : 0,
            duration_ms: duration ? Math.round(duration * 1000) : null,
          },
        });

        const saveTargetLower = getLabel(userRole, 'saveTarget').toLowerCase();
        toast({
          title: "Live Transcript Saved",
          description: `The live transcript has been saved to the ${saveTargetLower} record.`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        // Notify external window of success
        postMessageToExternalRecordingWindow({
          type: "LIVE_TRANSCRIPT_SAVED",
          success: true,
        });

        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent("transcriptionSaved"));
      } catch (error) {
        console.error("Error saving live transcript:", error);
        toast({
          title: "Save Failed",
          description: error.message || "Failed to save live transcript.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });

        postMessageToExternalRecordingWindow({
          type: "LIVE_TRANSCRIPT_SAVED",
          success: false,
          error: error.message,
        });
      }
    };

    window.addEventListener("saveLiveTranscript", handleSaveLiveTranscript);
    return () => {
      window.removeEventListener(
        "saveLiveTranscript",
        handleSaveLiveTranscript,
      );
    };
  }, [state, audioSaved, postMessageToExternalRecordingWindow]);

  // Patch the audio_url on a transcript entry once the audio upload finishes
  React.useEffect(() => {
    const handleLinkTranscript = async (event) => {
      const { sessionId, audioUrl } = event.detail;
      if (!sessionId || !audioUrl) return;

      const transcriptId = transcriptSessionMap.current[sessionId];
      if (!transcriptId) {
        console.warn(
          "🎤 linkLiveTranscriptToAudio: no transcript found for session",
          sessionId,
        );
        return;
      }

      try {
        const supabase = await getSupabase();
        const patientId = state?.id || refId || state?.scheduleId;

        const { data: existingData, error: fetchError } = await supabase
          .from(tableForPatientId(patientId))
          .select("transcripts")
          .eq("id", patientId)
          .maybeSingle();

        if (fetchError) throw fetchError;
        if (!Array.isArray(existingData?.transcripts)) return;

        // Extract filename from URL for the audio_file_name fallback in AudioPlayerModal
        const audioFileName = audioUrl.split("/").pop()?.split("?")[0] || "";
        const updatedTranscripts = existingData.transcripts.map((t) =>
          t.id === transcriptId
            ? {
                ...t,
                audio_url: audioUrl,
                audio_file_name: audioFileName || t.audio_file_name,
              }
            : t,
        );

        const { error: updateError } = await supabase
          .from(tableForPatientId(patientId))
          .update({ transcripts: updatedTranscripts })
          .eq("id", patientId);

        if (updateError) throw updateError;

        console.log(
          "🎤 Linked transcript",
          transcriptId,
          "to audio URL:",
          audioUrl,
        );
        delete transcriptSessionMap.current[sessionId];
        window.dispatchEvent(new CustomEvent("transcriptionSaved"));
      } catch (error) {
        console.error("🎤 Failed to link transcript to audio URL:", error);
      }
    };

    window.addEventListener("linkLiveTranscriptToAudio", handleLinkTranscript);
    return () => {
      window.removeEventListener(
        "linkLiveTranscriptToAudio",
        handleLinkTranscript,
      );
    };
  }, [state, refId]);

  // Handle live transcript updates from external window
  React.useEffect(() => {
    const handleLiveTranscriptUpdate = (event) => {
      console.log("🎤 Received live transcript update:", {
        textLength: event.detail?.text?.length || 0,
        textPreview: event.detail?.text?.substring(0, 100) || "(empty)",
      });
      // The live transcript is displayed in the external window itself
      // This handler ensures we can react to updates if needed in the parent
    };

    window.addEventListener("liveTranscriptUpdate", handleLiveTranscriptUpdate);
    return () => {
      window.removeEventListener(
        "liveTranscriptUpdate",
        handleLiveTranscriptUpdate,
      );
    };
  }, []);

  // Check external window status on component mount and sync state
  useEffect(() => {
    const checkWindowStatus = () => {
      // If we think the window should be open but it might be closed, check the ref
      if (externalWindowRef.current && externalWindowRef.current.closed) {
        console.log(
          "External window is closed but state shows it's open, updating...",
        );
        setRecordingState("closed");
        setIsExternalWindowOpen(false);

        // Persist the corrected state
        try {
          localStorage.setItem("audioRecorderRecordingState", "closed");
        } catch (error) {
          console.warn("Failed to persist recording state:", error);
        }
      }
    };

    // Check immediately
    checkWindowStatus();

    // Also set up periodic checking
    const interval = setInterval(checkWindowStatus, 5000); // Check every 5 seconds

    return () => clearInterval(interval);
  }, []);

  const setExternalWindowRef = (node) => {
    externalWindowRef.current = node;
    if (shouldOpenWindowRef.current && node?.openWindow) {
      const success = node.openWindow();
      if (success) {
        shouldOpenWindowRef.current = false;
        setIsOpening(false);
      } else {
        shouldOpenWindowRef.current = false;
        setPopupBlocked(true);
        setIsExternalWindowOpen(false);
        setIsOpening(false);
      }
    }
  };

  // Handle recording state changes from external window
  const handleRecordingStateChange = (newState) => {
    console.log("Recording state changed to:", newState);
    setRecordingState(newState);

    // Persist state to localStorage
    try {
      localStorage.setItem("audioRecorderRecordingState", newState);
    } catch (error) {
      console.warn("Failed to persist recording state:", error);
    }

    // Dispatch custom event for other components to react (e.g., RecordingBubble)
    window.dispatchEvent(new CustomEvent('recordingComponentStateChange', {
      detail: { state: newState, source: 'AudioRecorder' }
    }));
  };

  const handleMainButtonClick = async () => {
    // Ensure bubble has up-to-date patient context before opening.
    window.dispatchEvent(
      new CustomEvent("patientContextUpdate", {
        detail: {
          patientId,
          patientData: [state || {}],
        },
      }),
    );

    // Single start path across the app: open via RecordingBubble.
    const openEvent = new CustomEvent("recordingBubbleOpen", {
      detail: { source: "AudioRecorder", handled: false },
    });
    window.dispatchEvent(openEvent);

    if (!openEvent.detail?.handled) {
      toast({
        title: "Recording Bubble not available",
        description:
          "Please start recording from the red recording bubble on the patient page.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleExternalClose = async () => {
    setIsExternalWindowOpen(false);
    setPopupBlocked(false);
    setIsOpening(false);
    setRecordingState("closed");

    // Persist state to localStorage
    try {
      localStorage.setItem("audioRecorderRecordingState", "closed");
    } catch (error) {
      console.warn("Failed to persist recording state:", error);
    }

    // Dispatch custom event for other components to react (e.g., RecordingBubble)
    window.dispatchEvent(new CustomEvent('recordingComponentStateChange', {
      detail: { state: "closed", source: 'AudioRecorder' }
    }));

    // Check if there are backups to automatically save when external window closes
    // without saving audio (user closed the window without completing save)
    const currentPatientId = refId || state?.id || state?.scheduleId;
    if (currentPatientId && !audioSaved) {
      // Check for audio backup
      const allBackups =
        ExternalRecordingService.getAllBackupsForPatient(currentPatientId);
      const recoveryBackup =
        ExternalRecordingService.getRecoveryBackup(currentPatientId);

      if (allBackups?.length > 0 || recoveryBackup) {
        const backupToUse = recoveryBackup || allBackups[0];
        console.log(
          "💾 [AUTO-RECOVERY] External window closed without saving - found backup, auto-saving:",
          backupToUse,
        );

        // Get transcription backup data
        const backupStatus = getBackupStatus();
        const storedSessionId = localStorage.getItem(
          "currentRecordingSessionId",
        );
        const sessionId = backupStatus?.sessionId || storedSessionId;

        console.log("💾 [AUTO-RECOVERY] Checking transcription backup with:", {
          backupStatusSessionId: backupStatus?.sessionId,
          storedSessionId: storedSessionId,
          resolvedSessionId: sessionId,
          patientId: currentPatientId,
        });

        // Attempt to recover transcription from backup
        let transcriptionResult = null;
        try {
          // Calculate correct audio number for transcription search (not backup number)
          // First, get current audio count to determine which audio number this will be
          const supabase = await getSupabase();
          const currentRecord = await supabase
            .from(tableForPatientId(currentPatientId))
            .select("more_audios")
            .eq("id", currentPatientId)
            .maybeSingle();
          const currentAudioCount =
            currentRecord?.data?.more_audios?.length || 0;
          const correctAudioNumberForSearch = currentAudioCount + 1;

          console.log(
            "💾 [AUTO-RECOVERY] Calculated correct audio number for transcription search:",
            {
              currentAudioCount,
              correctAudioNumberForSearch,
              backupNumber: backupToUse?.backupNumber,
            },
          );

          const patientNameForSearch = state?.patient_name || "";
          const ehrIdForSearch =
            state?.EHR_ID || state?.[prompts?.[5]?.[0]] || "";
          const audioNumberForSearch = correctAudioNumberForSearch;
          const scheduledDateForSearch =
            state?.date_ || state?.scheduledDate || state?.date || "";
          const userSchemaForSearch = await getUserSchema();

          console.log(
            "💾 [AUTO-RECOVERY] Searching transcription backup with:",
            {
              patientNameForSearch,
              ehrIdForSearch,
              audioNumberForSearch,
              scheduledDateForSearch,
              userSchemaForSearch,
            },
          );

          // First, try search by patient name/EHR/date (most precise)
          if (
            patientNameForSearch &&
            ehrIdForSearch &&
            scheduledDateForSearch
          ) {
            const searchResult = await searchTranscriptionBackup(
              patientNameForSearch,
              ehrIdForSearch,
              audioNumberForSearch,
              scheduledDateForSearch,
              userSchemaForSearch || "public",
            );

            console.log(
              "💾 [AUTO-RECOVERY] Search by name/EHR/date result:",
              searchResult,
            );

            if (searchResult) {
              transcriptionResult = {
                success: true,
                transcriptText: searchResult.transcript_text || "",
                duration: searchResult.duration,
                metadata: searchResult.metadata,
                backupTimestamp: searchResult.backup_timestamp,
              };
            }
          }

          // If search didn't find anything, try to get ANY transcription backup for this patient
          if (!transcriptionResult?.success) {
            console.log(
              "💾 [AUTO-RECOVERY] No search result, trying getAnyTranscriptionBackup for patient:",
              currentPatientId,
            );
            const anyBackup = await getAnyTranscriptionBackup(currentPatientId);
            console.log(
              "💾 [AUTO-RECOVERY] getAnyTranscriptionBackup result:",
              anyBackup,
            );

            if (anyBackup) {
              transcriptionResult = {
                success: true,
                transcriptText:
                  anyBackup.transcript_text || anyBackup.text || "",
                duration: anyBackup.duration,
                metadata: anyBackup.metadata,
                backupTimestamp:
                  anyBackup.backup_timestamp || anyBackup.created_at,
              };
            } else if (sessionId) {
              console.log(
                "💾 [AUTO-RECOVERY] Trying session-based recovery with sessionId:",
                sessionId,
              );
              transcriptionResult = await recoverTranscriptionFromBackup(
                currentPatientId,
                sessionId,
              );
              console.log(
                "💾 [AUTO-RECOVERY] Session-based recovery result:",
                transcriptionResult,
              );
            }
          }
        } catch (transError) {
          console.error(
            "💾 [AUTO-RECOVERY] Error checking transcription backup:",
            transError,
          );
        }

        console.log(
          "💾 [AUTO-RECOVERY] Final transcription result:",
          transcriptionResult,
        );

        // Auto-save the backup audio (and transcription if available)
        // Use the same approach as the manual recovery button
        await handleRecoverRecordingSilent(currentPatientId, backupToUse);
      }
    }
  };

  // Silent recovery - auto-saves without showing the recovery UI
  // This replicates the logic from handleRecoverRecording for automatic recovery
  const handleRecoverRecordingSilent = async (patientId, backupData) => {
    if (!patientId) return;

    console.log(
      "💾 [AUTO-RECOVERY] Starting silent recovery for patient:",
      patientId,
    );

    // Get transcription backup using the same logic as manual recovery
    let transcriptionRecoveryData = null;
    try {
      const backupStatus = getBackupStatus();
      const storedSessionId = localStorage.getItem("currentRecordingSessionId");
      const sessionId = backupStatus?.sessionId || storedSessionId;

      console.log("💾 [AUTO-RECOVERY] Getting transcription backup with:", {
        backupStatusSessionId: backupStatus?.sessionId,
        storedSessionId: storedSessionId,
        resolvedSessionId: sessionId,
      });

      // Calculate correct audio number for transcription search (not backup number)
      // First, get current audio count to determine which audio number this will be
      const supabase = await getSupabase();
      const currentRecord = await supabase
        .from(tableForPatientId(patientId))
        .select("more_audios")
        .eq("id", patientId)
        .maybeSingle();
      const currentAudioCount = currentRecord?.data?.more_audios?.length || 0;
      const correctAudioNumberForSearch = currentAudioCount + 1;

      console.log(
        "💾 [AUTO-RECOVERY] Calculated correct audio number for transcription search:",
        {
          currentAudioCount,
          correctAudioNumberForSearch,
          backupNumber: backupData?.backupNumber,
        },
      );

      const patientNameForSearch = state?.patient_name || "";
      const ehrIdForSearch = state?.EHR_ID || state?.[prompts?.[5]?.[0]] || "";
      const audioNumberForSearch = correctAudioNumberForSearch;
      const scheduledDateForSearch =
        state?.date_ || state?.scheduledDate || state?.date || "";
      const userSchemaForSearch = await getUserSchema();

      // Try search by patient name/EHR/date first
      if (patientNameForSearch && ehrIdForSearch && scheduledDateForSearch) {
        const searchResult = await searchTranscriptionBackup(
          patientNameForSearch,
          ehrIdForSearch,
          audioNumberForSearch,
          scheduledDateForSearch,
          userSchemaForSearch || "public",
        );

        if (searchResult) {
          transcriptionRecoveryData = {
            success: true,
            transcriptText: searchResult.transcript_text || "",
            duration: searchResult.duration,
            metadata: searchResult.metadata,
            backupTimestamp: searchResult.backup_timestamp,
          };
        }
      }

      // If search didn't work, try any backup for patient
      if (!transcriptionRecoveryData?.success) {
        const anyBackup = await getAnyTranscriptionBackup(patientId);
        if (anyBackup) {
          transcriptionRecoveryData = {
            success: true,
            transcriptText: anyBackup.transcript_text || anyBackup.text || "",
            duration: anyBackup.duration,
            metadata: anyBackup.metadata,
            backupTimestamp: anyBackup.backup_timestamp || anyBackup.created_at,
          };
        } else if (sessionId) {
          transcriptionRecoveryData = await recoverTranscriptionFromBackup(
            patientId,
            sessionId,
          );
        }
      }

      console.log(
        "💾 [AUTO-RECOVERY] Transcription recovery result:",
        transcriptionRecoveryData,
      );
    } catch (transError) {
      console.error(
        "💾 [AUTO-RECOVERY] Error getting transcription backup:",
        transError,
      );
    }

    console.log(
      "💾 [AUTO-RECOVERY] Final transcription data to save:",
      transcriptionRecoveryData,
    );

    try {
      const backupToUse = backupData;
      if (!backupToUse) {
        console.warn("💾 [AUTO-RECOVERY] No backup data available");
        return;
      }

      const supabase = await getSupabase();

      // Get current audio count to determine the audio number
      const currentRecord = await supabase
        .from(tableForPatientId(patientId))
        .select("more_audios, transcripts")
        .eq("id", patientId)
        .maybeSingle();

      const currentAudioCount = currentRecord?.data?.more_audios?.length || 0;
      const nextAudioNumber = currentAudioCount + 1;
      const sequentialName = `Audio ${nextAudioNumber}`;

      const audioData = {
        name: `${sequentialName} (backup)`,
        description: "Recovered from external window close",
        url: backupToUse.url,
        duration: backupToUse.duration || 0,
        source: "backup_restored",
        timestamp: backupToUse.timestamp || new Date().toISOString(),
        uploaded_at: new Date().toISOString(),
        verified: false,
        type: "audio",
        patient_id: patientId,
        recording_date: new Date().toISOString(),
        displayName: `${sequentialName} (backup)`,
        isRecovered: true,
        backupNumber: backupToUse.backupNumber,
      };

      // Update database with recovered audio
      const updateResult = await supabase
        .from(tableForPatientId(patientId))
        .select("more_audios")
        .eq("id", patientId)
        .maybeSingle();

      let newAudios = [];
      if (updateResult?.data?.more_audios) {
        newAudios = [...updateResult.data.more_audios, audioData];
      } else {
        newAudios = [audioData];
      }

      const { error: updateError } = await supabase
        .from(tableForPatientId(patientId))
        .update({ more_audios: newAudios })
        .eq("id", patientId);

      if (updateError) {
        throw new Error(`Database update failed: ${updateError.message}`);
      }

      console.log("💾 [AUTO-RECOVERY] Audio saved to patient record");

      // Clear recovery data after successful save
      ExternalRecordingService.clearRecoveryBackup(patientId);

      // Clean up all backup keys for this patient
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(`backup_${patientId}_`)) {
          localStorage.removeItem(key);
          i--;
        }
      }

      // Mark this backup as recovered to prevent showing button again on refresh
      const recoveredUrlKey = backupToUse?.url
        ? backupToUse.url.split("?")[0]
        : "unknown";
      const markersJson =
        localStorage.getItem(`recovered_${patientId}`) || "{}";
      const markers = JSON.parse(markersJson);
      markers[recoveredUrlKey] = true;
      localStorage.setItem(`recovered_${patientId}`, JSON.stringify(markers));

      // Save recovered transcription to database if available
      console.log("💾 [AUTO-RECOVERY] Checking transcription save with:", {
        transcriptionRecoveryData,
        hasTranscriptionData: !!transcriptionRecoveryData,
        hasSuccess: transcriptionRecoveryData?.success,
        transcriptTextLength: transcriptionRecoveryData?.transcriptText?.length,
        metadata: transcriptionRecoveryData?.metadata,
      });

      if (transcriptionRecoveryData && transcriptionRecoveryData.success) {
        try {
          // Fetch existing transcripts
          const { data: existingData, error: fetchError } = await supabase
            .from(tableForPatientId(patientId))
            .select("transcripts")
            .eq("id", patientId)
            .maybeSingle();

          console.log("💾 [AUTO-RECOVERY] Existing transcripts fetch result:", {
            data: existingData,
            error: fetchError,
          });

          if (fetchError) {
            console.error("Error fetching existing transcripts:", fetchError);
          } else {
            const uniqueId =
              typeof crypto !== "undefined" && crypto.randomUUID
                ? crypto.randomUUID()
                : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
                    /[xy]/g,
                    function (c) {
                      var r = (Math.random() * 16) | 0,
                        v = c == "x" ? r : (r & 0x3) | 0x8;
                      return v.toString(16);
                    },
                  );

            const recoveredSequentialName = `Audio ${nextAudioNumber} (backup)`;

            // Handle different metadata structures from backup
            const metadata = transcriptionRecoveryData.metadata || {};
            // If metadata is a string (JSON), parse it
            let parsedMetadata = metadata;
            if (typeof metadata === "string") {
              try {
                parsedMetadata = JSON.parse(metadata);
              } catch (e) {
                console.warn(
                  "💾 [AUTO-RECOVERY] Failed to parse metadata JSON:",
                  e,
                );
              }
            }

            const transcriptionToSave = {
              transcript: transcriptionRecoveryData.transcriptText
                ? transcriptionRecoveryData.transcriptText
                    .split("\n")
                    .filter(Boolean)
                : [],
              facts: parsedMetadata?.facts || metadata?.facts || [],
              soap_notes:
                parsedMetadata?.soapNotes ||
                metadata?.soap_notes ||
                metadata?.soapNotes ||
                null,
              audio_file_name: recoveredSequentialName,
              audio_url: backupToUse.url,
              audio_source: "live_transcript",
              duration:
                backupToUse.duration || transcriptionRecoveryData.duration || 0,
              id: uniqueId,
              created_at:
                transcriptionRecoveryData.backupTimestamp ||
                new Date().toISOString(),
              isRecovered: true,
              is_live_transcript: true,
            };

            let newTranscripts = [];
            if (existingData && existingData.transcripts) {
              if (Array.isArray(existingData.transcripts)) {
                newTranscripts = [
                  ...existingData.transcripts,
                  transcriptionToSave,
                ];
              } else {
                newTranscripts = [
                  existingData.transcripts,
                  transcriptionToSave,
                ];
              }
            } else {
              newTranscripts = [transcriptionToSave];
            }

            const { error: updateError } = await supabase
              .from(tableForPatientId(patientId))
              .update({ transcripts: newTranscripts })
              .eq("id", patientId);

            if (updateError) {
              console.error(
                "Error saving recovered transcription:",
                updateError,
              );
            } else {
              console.log("💾 [AUTO-RECOVERY] Transcription saved to database");
            }
          }
        } catch (transSaveError) {
          console.error(
            "Error saving recovered transcription:",
            transSaveError,
          );
        }
      }

      console.log("💾 [AUTO-RECOVERY] Silent recovery completed successfully");

      // Trigger refresh
      window.dispatchEvent(
        new CustomEvent("audioRecordingComplete", {
          detail: {
            patientId: patientId,
            audioUrl: backupToUse.url,
            timestamp: new Date().toISOString(),
            isRecovered: true,
          },
        }),
      );

      // Show toast notification that backup was saved
      const saveTargetLower = getLabel(userRole, 'saveTarget').toLowerCase();
      toast({
        title: "Audio Recovered",
        description: `Recording was automatically saved from backup to the ${saveTargetLower} record.`,
        status: "info",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("💾 [AUTO-RECOVERY] Silent recovery failed:", error);
    }
  };

  const handlePopupBlocked = () => {
    setPopupBlocked(true);
    setIsExternalWindowOpen(false);
    setIsOpening(false);
  };

  const retryOpenWindow = () => {
    setPopupBlocked(false);
    handleMainButtonClick();
  };

  // Load ai_recap_enabled from schema_settings once on mount
  useEffect(() => {
    (async () => {
      try {
        const supabase = await supabasePublic();
        const { data } = await supabase
          .from("schema_settings")
          .select("ai_recap_enabled")
          .eq("schema_name", supabaseSchema || "public")
          .single();
        if (data && data.ai_recap_enabled !== undefined) {
          setAiRecapEnabled(data.ai_recap_enabled ?? true);
        }
      } catch (_) {}
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for displayMediaShared event to track what's being shared for system audio
  useEffect(() => {
    // First check localStorage for any existing shared display info from a previous session
    try {
      const stored = localStorage.getItem("sharedDisplayInfo");
      if (stored) {
        const info = JSON.parse(stored);
        // Only use stored info if it's recent (within last 5 minutes)
        if (info?.timestamp) {
          const storedTime = new Date(info.timestamp).getTime();
          const now = Date.now();
          if (now - storedTime < 5 * 60 * 1000) {
            console.log("🖥️ [AudioRecorder] Found stored display info:", info);
            setSharedDisplayInfo(info);
          } else {
            // Clear expired data
            localStorage.removeItem("sharedDisplayInfo");
          }
        }
      }
    } catch (e) {
      console.warn("Could not read sharedDisplayInfo from localStorage:", e);
    }

    // Then listen for live updates via custom event
    const handleDisplayMediaShared = (event) => {
      console.log(
        "🖥️ [AudioRecorder] Received displayMediaShared event:",
        event.detail,
      );
      if (event.detail?.displayInfo) {
        setSharedDisplayInfo(event.detail.displayInfo);
      }
    };
    window.addEventListener("displayMediaShared", handleDisplayMediaShared);
    return () => {
      window.removeEventListener(
        "displayMediaShared",
        handleDisplayMediaShared,
      );
    };
  }, []);

  const generateRecap = async (transcriptText, patientId, sessionId) => {
    console.log("[RECAP] generateRecap called", {
      hasText: !!transcriptText?.trim(),
      patientId,
      sessionId,
    });
    if (!transcriptText?.trim()) {
      console.warn("[RECAP] Aborted: no transcript text");
      return;
    }
    try {
      const sendToPopup = (msg) =>
        postMessageToExternalRecordingWindow(msg);
      console.log("[RECAP DEBUG] calling runLiveAiAnalysis", {
        transcriptLength: transcriptText?.length || 0,
      });
      // A5.1: clinicalEnabled false — popup's per-section A5 pipeline owns SOAP/clinical_tabs
      // generation. Setting clinicalEnabled=true here triggers a SECOND Sonnet call against the
      // old mega-prompt endpoint and posts LIVE_CLINICAL_TABS_READY → overwrites the popup's
      // A5 result with a 9-key payload. Parent now only generates recap (summary + key_takeaways
      // + action_items) for the parent's AI Assistant panel.
      const json = await runLiveAiAnalysis(sendToPopup, transcriptText, {
        clinicalEnabled: false,
        modelChoice: 1,
      });
      console.log("[RECAP DEBUG] AI response keys:", Object.keys(json || {}));
      const recap = json?.recap;
      if (!recap) {
        throw new Error("No recap in API response");
      }
      console.log(
        "[RECAP] Recap received from API:",
        JSON.stringify(recap).slice(0, 200),
      );

      // Save recap as a separate entry in the transcripts array, linked to the live transcript
      if (!patientId) {
        console.warn("[RECAP] No patientId — skipping DB save");
      } else {
        try {
          const supabase = await getSupabase();
          console.log("[RECAP] Fetching transcripts for patient", patientId);
          const { data: patientData, error: fetchErr } = await supabase
            .from(tableForPatientId(patientId))
            .select("transcripts")
            .eq("id", patientId)
            .maybeSingle();
          console.log(
            "[RECAP] Fetch result — error:",
            fetchErr?.message,
            "transcripts type:",
            typeof patientData?.transcripts,
            "isArray:",
            Array.isArray(patientData?.transcripts),
            "length:",
            patientData?.transcripts?.length,
          );
          if (fetchErr) {
            console.error(
              "[RECAP] Fetch error — aborting save:",
              fetchErr.message,
            );
          } else {
            const existingTranscripts = Array.isArray(patientData?.transcripts)
              ? patientData.transcripts
              : [];
            // Resolve linked transcript ID: prefer session map, fall back to most recent
            // live transcript that doesn't already have a recap
            let linkedTranscriptId = sessionId
              ? (transcriptSessionMap.current[sessionId] ?? null)
              : null;
            if (!linkedTranscriptId) {
              const linkedIds = new Set(
                existingTranscripts
                  .filter((t) => t.is_ai_recap && t.linked_transcript_id)
                  .map((t) => t.linked_transcript_id),
              );
              const candidate = existingTranscripts
                .filter((t) => t.is_live_transcript && !linkedIds.has(t.id))
                .sort(
                  (a, b) => new Date(b.created_at) - new Date(a.created_at),
                )[0];
              linkedTranscriptId = candidate?.id ?? null;
              console.log(
                "[RECAP] Session map empty — falling back to most recent unlinked live transcript:",
                linkedTranscriptId,
              );
            }
            console.log(
              "[RECAP] sessionId:",
              sessionId,
              "→ linkedTranscriptId:",
              linkedTranscriptId,
              "| sessionMap keys:",
              Object.keys(transcriptSessionMap.current),
            );
            // Only treat as duplicate if the linked ID is non-null and already has a recap
            const alreadyExists =
              linkedTranscriptId !== null &&
              existingTranscripts.some(
                (t) =>
                  t.is_ai_recap &&
                  t.linked_transcript_id === linkedTranscriptId,
              );
            // Strip any stale null-linked recap entries (from previous broken saves)
            const cleanedTranscripts = existingTranscripts.filter(
              (t) => !(t.is_ai_recap && t.linked_transcript_id === null),
            );
            console.log(
              "[RECAP] alreadyExists:",
              alreadyExists,
              "| existingTranscripts count:",
              existingTranscripts.length,
              "| stale null entries removed:",
              existingTranscripts.length - cleanedTranscripts.length,
            );
            if (alreadyExists) {
              console.warn("[RECAP] Duplicate recap detected — skipping save");
            } else {
              const recapEntry = {
                id: crypto.randomUUID
                  ? crypto.randomUUID()
                  : Date.now().toString(),
                is_ai_recap: true,
                linked_transcript_id: linkedTranscriptId,
                patient_id: patientId?.toString() || "",
                summary: recap.summary || "",
                key_takeaways: recap.key_takeaways || [],
                action_items: recap.action_items || [],
                created_at: new Date().toISOString(),
              };
              console.log(
                "[RECAP] Saving recapEntry:",
                JSON.stringify(recapEntry).slice(0, 300),
              );
              const { error: saveErr } = await supabase
                .from(tableForPatientId(patientId))
                .update({ transcripts: [...cleanedTranscripts, recapEntry] })
                .eq("id", patientId);
              if (saveErr) {
                console.error("[RECAP] DB update error:", saveErr.message);
              } else {
                console.log("[RECAP] ✅ Recap saved successfully to DB");
                toast({
                  title: "AI Recap Saved",
                  description:
                    "The AI recap has been saved to the live transcript.",
                  status: "success",
                  duration: 3000,
                  isClosable: true,
                });
                window.dispatchEvent(new CustomEvent("transcriptionSaved"));
              }
            }
          }
        } catch (saveErr) {
          console.error(
            "[RECAP] Exception during DB save:",
            saveErr.message,
            saveErr.stack,
          );
        }
      }
    } catch (err) {
      console.error("[RECAP] API call failed:", err.message);
    }
  };

  const generateRecapRef = React.useRef(generateRecap);
  generateRecapRef.current = generateRecap;

  // Direct upload path: popup posts AUDIO_SAVED_SUCCESS to opener (no onAudioSaved callback).
  React.useEffect(() => {
    const onExternalPopupSaved = (event) => {
      const d = event.data;
      if (!d || d.type !== "AUDIO_SAVED_SUCCESS") return;
      const txt = (d.liveTranscriptText || "").trim();
      console.log("[RECAP DEBUG] window message AUDIO_SAVED_SUCCESS", {
        hasLiveTranscriptText: !!txt,
        liveTranscriptLength: txt.length,
        sessionId: d.sessionId || null,
        patientId: d.patientId || null,
        wasDirectUpload: !!d.wasDirectUpload,
      });
      if (!txt) return;
      const pid =
        d.patientId || state?.id || state?.scheduleId || refId || null;
      const sid = d.sessionId || null;
      generateRecapRef.current(txt, pid, sid);
    };
    window.addEventListener("message", onExternalPopupSaved);
    return () => window.removeEventListener("message", onExternalPopupSaved);
  }, [state?.id, state?.scheduleId, refId]);

  const handleAudioSaved = async (result) => {
    console.log("🎤 Audio saved result:", result);

    if (result.success) {
      // Set audio saved data for UI
      setAudioSaved(result);
      setSelectedPatient(state);
      setLastElapsedTime(result.audioData?.duration || 0);

      const saveTargetLower = getLabel(userRole, 'saveTarget').toLowerCase();
      toast({
        title: "Audio Saved",
        description: `Recording has been saved to the ${saveTargetLower}'s audio files.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Send audio URL back to external window so it can use it for transcription
      const audioUrl = result.audioUrl || result.audioData?.audioUrl;
      if (audioUrl) {
        postMessageToExternalRecordingWindow({
          type: "AUDIO_SAVED_SUCCESS",
          audioUrl: audioUrl,
          audioData: result.audioData,
        });
        console.log(
          "🎤 Sent audio URL to external window for transcription:",
          audioUrl,
        );

        // Transcript was already dispatched in useWindowMessaging.js before the audio
        // upload started, so no need to dispatch it again here.
      }

      // After Stop & Save: run recap + clinical tabs (same Claude API) when there is live transcript text.
      const liveText = result.liveTranscriptText?.trim();
      console.log("[RECAP DEBUG] handleAudioSaved liveText length:", liveText?.length || 0);
      if (liveText) {
        const sid = result.sessionId || null;
        const pid = result.patientId || state?.id || null;
        console.log("[RECAP DEBUG] handleAudioSaved calling generateRecap", { pid, sid });
        generateRecap(liveText, pid, sid);
      }

      // Don't automatically start transcription - let user choose in external window
      // Audio URL is stored in audioSaved state for optional transcription
    } else {
      toast({
        title: "Save Failed",
        description:
          result.error || "Failed to save recording to patient record.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  handleAudioSavedRef.current = handleAudioSaved;

  // Handle manual transcription request from user after audio is saved
  const handleTranscribeSavedAudio = async () => {
    const audioUrl = audioSaved?.audioUrl || audioSaved?.audioData?.audioUrl;
    if (!audioUrl) {
      toast({
        title: "No Audio Available",
        description: "Audio file not found. Please try recording again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Extract audio metadata for the transcription record
    const audioMetadata = {
      audio_duration: audioSaved.audioData?.duration || 0,
      audio_file_name:
        audioSaved.audioData?.name ||
        audioSaved.audioData?.displayName ||
        "Recording",
      audio_source: audioSaved.audioData?.source || "recording",
    };

    await startTranscription(audioUrl, state, audioMetadata);
  };

  // Start transcription using Corti integration service
  const startTranscription = async (
    audioUrl,
    patientContext,
    audioMetadata = {},
  ) => {
    if (!audioUrl || !patientContext) {
      console.error(
        "❌ Missing audio URL or patient context for transcription",
      );
      return;
    }

    setIsTranscribing(true);
    setTranscriptionError(null);
    setTranscriptionProgress({
      stage: "starting",
      message: "Initializing transcription...",
    });

    // Notify external window that transcription started
    postMessageToExternalRecordingWindow({
      type: "TRANSCRIPTION_STARTED",
    });

    try {
      // Create patient context for transcription
      const context = {
        id: patientContext.id || refId || patientContext.scheduleId,
        name:
          patientContext[prompts[0][0]] ||
          patientContext.patient_name ||
          "Unknown Patient",
        ...patientContext,
      };

      console.log("🎤 Starting transcription with context:", context);

      beginActivity("transcribing");
      const result = await cortiIntegrationService.transcribeAudioFile(
        audioUrl,
        context,
        (progress) => {
          setTranscriptionProgress(progress);
          console.log("📊 Transcription progress:", progress);

          // Send progress to external window
          postMessageToExternalRecordingWindow({
            type: "TRANSCRIPTION_PROGRESS",
            data: progress,
          });
        },
      );

      if (result.success) {
        console.log("✅ Transcription completed successfully:", result);

        // Set transcription results
        setTranscriptionResults(result);
        setTranscript(result.transcript || []);
        setFacts(result.facts || []);
        setSoapNotes(result.soap_notes || null);

        // Send results to external window if open
        postMessageToExternalRecordingWindow({
          type: "TRANSCRIPTION_RESULTS",
          data: result,
        });
        console.log("📤 Sending transcription results to external window");

        // Show success message and results
        toast({
          title: "Transcription Complete",
          description:
            "Audio has been transcribed successfully. Results sent to recorder window.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });

        setShowResults(true);

        // Save transcription to database so it appears in the table
        try {
          if (!context.id) {
            throw new Error("Missing patient ID context for saving");
          }

          const supabase = await getSupabase();
          // First fetch existing transcripts to append if necessary, or just overwrite/set
          const { data: existingData, error: fetchError } = await supabase
            .from(tableForPatientId(context.id))
            .select("transcripts")
            .eq("id", context.id)
            .maybeSingle();

          if (fetchError) {
            console.error("Error fetching existing transcripts:", fetchError);
            throw fetchError;
          }

          // Generate a unique ID (fallback for older browsers)
          const uniqueId =
            typeof crypto !== "undefined" && crypto.randomUUID
              ? crypto.randomUUID()
              : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
                  /[xy]/g,
                  function (c) {
                    var r = (Math.random() * 16) | 0,
                      v = c == "x" ? r : (r & 0x3) | 0x8;
                    return v.toString(16);
                  },
                );

          // Calculate next audio number for sequential naming
          let nextAudioNumber = 1;
          if (existingData && existingData.transcripts) {
            const currentTranscripts = Array.isArray(existingData.transcripts)
              ? existingData.transcripts
              : [existingData.transcripts];

            // Try to find max audio number from existing names like "Audio 1", "Audio 2"
            const audioNumbers = currentTranscripts
              .map((t) => {
                const name = t.audio_file_name || "";
                const match = name.match(/Audio\s*(\d+)/i);
                return match ? parseInt(match[1], 10) : 0;
              })
              .filter((n) => n > 0);

            if (audioNumbers.length > 0) {
              nextAudioNumber = Math.max(...audioNumbers) + 1;
            } else {
              nextAudioNumber = currentTranscripts.length + 1;
            }
          }

          const sequentialName = `Audio ${nextAudioNumber}`;

          // Prepare the result with a unique ID and timestamp
          const transcriptionToSave = {
            ...result,
            ...audioMetadata, // Include audio metadata (duration, filename, source)
            audio_file_name: sequentialName, // Override filename with sequential name
            audio_url:
              audioMetadata.audio_url ||
              audioSaved?.audioUrl ||
              audioSaved?.audioData?.url ||
              "",
            id: result.id || uniqueId,
            created_at: result.created_at || new Date().toISOString(),
          };

          let newTranscripts = [];
          if (existingData && existingData.transcripts) {
            if (Array.isArray(existingData.transcripts)) {
              newTranscripts = [
                ...existingData.transcripts,
                transcriptionToSave,
              ];
            } else {
              // Convert existing single object to array and append
              newTranscripts = [existingData.transcripts, transcriptionToSave];
            }
          } else {
            // Start as an array containing this result
            newTranscripts = [transcriptionToSave];
          }

          const { error: updateError } = await supabase
            .from(tableForPatientId(context.id))
            .update({ transcripts: newTranscripts })
            .eq("id", context.id);

          if (updateError) {
            console.error(
              "Error saving transcription to database:",
              updateError,
            );
            throw updateError;
          } else {
            console.log("✅ Transcription saved to database successfully");
            trackUserEvent('transcript_complete', {
              metadata: {
                source: result?.audio_source || 'recorded_audio',
                transcript_segments: Array.isArray(result?.transcript) ? result.transcript.length : null,
                audio_filename: sequentialName,
              },
            });
            // Dispatch event to notify ResultsTable
            window.dispatchEvent(new CustomEvent("transcriptionSaved"));

            // Auto-generate Copilot tabs if user has copilot_enabled = true
            try {
              const { data: { session } } = await supabase.auth.getSession();
              if (session?.user) {
                const { data: userData } = await supabase
                  .from("users")
                  .select("copilot_enabled")
                  .eq("id", session.user.id)
                  .maybeSingle();

                // Stripe paywall removed (2026-06-25, Brett). Premium Copilot for everyone.
                if (true) {
                  const baseUrl = getBackendURL();
                  const audioPublicUrl = transcriptionToSave.audio_url || "";
                  
                  if (audioPublicUrl) {
                    const flatText = extractTranscriptPlainTextForClinicalAi(transcriptionToSave);

                    generateClinicalTabsAndPersistCopilotSnapshot({
                      transcriptText: flatText,
                      backendBaseUrl: baseUrl,
                      audioRow: null,
                      transcriptRow: transcriptionToSave,
                      audioPublicUrl,
                      userId: session.user.id,
                      userJwtToken: session.access_token,
                      patientId: context.id,
                      sessionId: transcriptionToSave.session_id,
                    }).then((res) => {
                      if (res.ok) {
                        console.log("✅ [AudioRecorder] Auto-generated clinical tabs:", res);
                        window.dispatchEvent(new CustomEvent("copilotTabsGenerated"));
                      } else {
                        console.warn("⚠️ [AudioRecorder] Auto-generation failed:", res.error);
                      }
                    });
                  }
                }
              }
            } catch (copilotErr) {
              console.error("❌ [AudioRecorder] Error auto-generating clinical tabs:", copilotErr);
            }
          }
        } catch (dbError) {
          console.error("Error in database save operation:", dbError);
          toast({
            title: "Save Warning",
            description:
              "Transcription generated but failed to save to history.",
            status: "warning",
            duration: 5000,
            isClosable: true,
          });
        }
      } else {
        throw new Error(result.error || "Transcription failed");
      }
    } catch (error) {
      console.error("❌ Transcription error:", error);
      const errorResult =
        cortiIntegrationService.handleTranscriptionError(error);

      setTranscriptionError(errorResult);

      // Send error to external window
      postMessageToExternalRecordingWindow({
        type: "TRANSCRIPTION_ERROR",
        data: errorResult,
      });

      toast({
        title: "Transcription Failed",
        description: errorResult.error,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsTranscribing(false);
      setTranscriptionProgress(null);
      endActivity("transcribing");
    }
  };

  // PDF export functions
  const handleTranscribeAudio = async (patientContext = null) => {
    // Use provided patient context or fall back to current state
    const currentPatient = patientContext || selectedPatient || state;

    console.log("🔍 handleTranscribeAudio called with:", {
      patientContext,
      selectedPatient: !!selectedPatient,
      state: !!state,
      currentPatient: !!currentPatient,
      patientName: currentPatient?.[prompts[0][0]],
    });

    if (!currentPatient) {
      console.error("❌ No patient data available");
      toast({
        title: "No Patient Selected",
        description: "Please select a patient to transcribe audio.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Check credit limit before proceeding
    const creditLimitExceeded = await checkCreditLimitAndUpdateUI(toast);
    if (creditLimitExceeded) {
      return;
    }

    // Set the patient context for the modal
    setSelectedPatient(currentPatient);
    console.log("✅ Patient context set for transcription");
    setIsTranscribeModalOpen(true);
  };

  // Function to clean and validate SOAP notes
  const cleanSoapNotes = (soapNotes) => {
    if (!soapNotes) return null;

    console.log("🧹 Cleaning SOAP notes:", soapNotes);

    const cleaned = {};

    // Clean transcript_only
    if (soapNotes.transcript_only) {
      try {
        // Check if it's JSON schema (contains "properties" and "type")
        if (
          typeof soapNotes.transcript_only === "string" &&
          soapNotes.transcript_only.includes('"properties"') &&
          soapNotes.transcript_only.includes('"type"')
        ) {
          console.log("⚠️ Detected JSON schema in transcript_only, skipping");
          cleaned.transcript_only = null;
        } else {
          cleaned.transcript_only = soapNotes.transcript_only;
          console.log("✅ transcript_only cleaned successfully");
        }
      } catch (error) {
        console.error("Error cleaning transcript_only:", error);
        cleaned.transcript_only = null;
      }
    }

    // Clean facts_only
    if (soapNotes.facts_only) {
      try {
        // Check if it's JSON schema (contains "properties" and "type")
        if (
          typeof soapNotes.facts_only === "string" &&
          soapNotes.facts_only.includes('"properties"') &&
          soapNotes.facts_only.includes('"type"')
        ) {
          console.log("⚠️ Detected JSON schema in facts_only, skipping");
          cleaned.facts_only = null;
        } else {
          cleaned.facts_only = soapNotes.facts_only;
          console.log("✅ facts_only cleaned successfully");
        }
      } catch (error) {
        console.error("Error cleaning facts_only:", error);
        cleaned.facts_only = null;
      }
    }

    console.log("🧹 Cleaned SOAP notes result:", cleaned);
    return cleaned;
  };

  const generatePDF = () => {
    const doc = new jsPDF();

    // Add title
    doc.setFontSize(20);
    doc.text("Medical Report Transcription", 20, 20);

    // Add patient info
    doc.setFontSize(14);
    doc.text(
      `Patient: ${selectedPatient?.[prompts[0][0]] || "Unknown"}`,
      20,
      35,
    );
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, 45);
    doc.text(`Duration: ${lastElapsedTime || 0} seconds`, 20, 55);

    // Add transcript
    doc.setFontSize(16);
    doc.text("Transcript:", 20, 75);
    doc.setFontSize(10);
    let yPos = 85;
    transcript?.forEach((line, index) => {
      if (yPos > 280) {
        doc.addPage();
        yPos = 20;
      }
      // Split long lines to fit page width
      const lines = doc.splitTextToSize(line, 170);
      lines.forEach((splitLine) => {
        if (yPos > 280) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(splitLine, 20, yPos);
        yPos += 6;
      });
    });

    // Add facts
    doc.addPage();
    doc.setFontSize(16);
    doc.text("Extracted Facts:", 20, 20);
    doc.setFontSize(10);
    yPos = 30;
    facts?.forEach((fact, index) => {
      if (yPos > 280) {
        doc.addPage();
        yPos = 20;
      }
      const factText = `${index + 1}. ${fact.text} (Category: ${fact.group})`;
      const lines = doc.splitTextToSize(factText, 170);
      lines.forEach((line) => {
        if (yPos > 280) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(line, 20, yPos);
        yPos += 6;
      });
      yPos += 3; // Add space between facts
    });

    // Add SOAP Notes
    if (soapNotes && (soapNotes.transcript_only || soapNotes.facts_only)) {
      doc.addPage();
      doc.setFontSize(16);
      doc.text("SOAP Notes:", 20, 20);
      doc.setFontSize(10);
      yPos = 30;

      if (soapNotes.transcript_only) {
        doc.setFontSize(12);
        doc.text("Transcript-Based SOAP Notes:", 20, yPos);
        yPos += 10;
        doc.setFontSize(10);
        const soapLines = doc.splitTextToSize(soapNotes.transcript_only, 170);
        soapLines.forEach((line) => {
          if (yPos > 280) {
            doc.addPage();
            yPos = 20;
          }
          doc.text(line, 20, yPos);
          yPos += 6;
        });
        yPos += 10;
      }

      if (soapNotes.facts_only) {
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }
        doc.setFontSize(12);
        doc.text("Facts-Based SOAP Notes:", 20, yPos);
        yPos += 10;
        doc.setFontSize(10);
        const soapLines = doc.splitTextToSize(soapNotes.facts_only, 170);
        soapLines.forEach((line) => {
          if (yPos > 280) {
            doc.addPage();
            yPos = 20;
          }
          doc.text(line, 20, yPos);
          yPos += 6;
        });
      }
    }

    doc.save("medical-report.pdf");
  };

  // Generate PDF with real transcription data
  const generatePDFWithRealData = () => {
    const doc = new jsPDF();

    // Add title
    doc.setFontSize(20);
    doc.text("Medical Report Transcription", 20, 20);

    // Add patient info
    doc.setFontSize(14);
    doc.text(
      `Patient: ${selectedPatient?.[prompts[0][0]] || "Unknown"}`,
      20,
      35,
    );
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, 45);
    doc.text(`Duration: ${lastElapsedTime || 0} seconds`, 20, 55);

    // Add transcript with real data
    doc.setFontSize(16);
    doc.text("Transcript:", 20, 75);
    doc.setFontSize(10);
    let yPos = 85;

    // Use continuous transcript if available, otherwise join lines
    let transcriptText = "";
    if (transcriptionResults?.continuous_transcript) {
      transcriptText = transcriptionResults.continuous_transcript;
    } else {
      const transcriptData =
        transcript.length > 0
          ? transcript
          : transcriptionResults?.transcript || [];
      transcriptText = transcriptData
        .map((line) =>
          typeof line === "string" ? line : line.transcript || line.text || "",
        )
        .join(" ");
    }

    const lines = doc.splitTextToSize(transcriptText, 170);
    lines.forEach((splitLine) => {
      if (yPos > 280) {
        doc.addPage();
        yPos = 20;
      }
      doc.text(splitLine, 20, yPos);
      yPos += 6;
    });

    // Add facts with real data
    doc.addPage();
    doc.setFontSize(16);
    doc.text("Extracted Facts:", 20, 20);
    doc.setFontSize(10);
    yPos = 30;

    const factsData =
      facts.length > 0 ? facts : transcriptionResults?.facts || [];

    // PROCESS FACTS: Split long comma-separated facts into multiple facts for better display
    const processedFacts = [];
    factsData.forEach((fact) => {
      const text = typeof fact === "string" ? fact : fact.text || "";
      const category =
        typeof fact === "object"
          ? fact.category || fact.group || "general"
          : "general";

      // If text is long and has multiple commas, try to split it into logical items
      // This handles cases where extraction produced one giant "list" fact
      if (text.length > 60 && (text.match(/,/g) || []).length >= 2) {
        // Split by comma, but be careful not to split too aggressively
        // We only split if the segments look like items (e.g., have length)
        const parts = text
          .split(/,(?![^(]*\))/) // Split by comma not inside parentheses
          .map((p) => p.trim())
          .filter((p) => p.length > 0);

        if (parts.length > 1) {
          parts.forEach((part) => {
            // Only push if part has some substance
            if (part.length > 2) {
              processedFacts.push({ text: part, category });
            }
          });
        } else {
          processedFacts.push({ text, category });
        }
      } else {
        processedFacts.push({ text, category });
      }
    });

    processedFacts.forEach((fact, index) => {
      if (yPos > 280) {
        doc.addPage();
        yPos = 20;
      }

      const text = fact.text;
      const category = fact.category;

      const factText = `${index + 1}. ${text} (Category: ${category})`;
      const lines = doc.splitTextToSize(factText, 170);
      lines.forEach((line) => {
        if (yPos > 280) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(line, 20, yPos);
        yPos += 6;
      });
      yPos += 3;
    });

    // Add SOAP Notes with real data
    const soapData = soapNotes || transcriptionResults?.soap_notes;
    if (soapData && (soapData.transcript_only || soapData.facts_only)) {
      doc.addPage();
      doc.setFontSize(16);
      doc.text("SOAP Notes:", 20, 20);
      doc.setFontSize(10);
      yPos = 30;

      if (soapData.transcript_only) {
        doc.setFontSize(12);
        doc.text("Transcript-Based SOAP Notes:", 20, yPos);
        yPos += 10;
        doc.setFontSize(10);
        const soapLines = doc.splitTextToSize(soapData.transcript_only, 170);
        soapLines.forEach((line) => {
          if (yPos > 280) {
            doc.addPage();
            yPos = 20;
          }
          doc.text(line, 20, yPos);
          yPos += 6;
        });
        yPos += 10;
      }

      if (soapData.facts_only) {
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }
        doc.setFontSize(12);
        doc.text("Facts-Based SOAP Notes:", 20, yPos);
        yPos += 10;
        doc.setFontSize(10);
        const soapLines = doc.splitTextToSize(soapData.facts_only, 170);
        soapLines.forEach((line) => {
          if (yPos > 280) {
            doc.addPage();
            yPos = 20;
          }
          doc.text(line, 20, yPos);
          yPos += 6;
        });
      }
    }

    doc.save("medical-report.pdf");

    // Show success message
    toast({
      title: "PDF Generated",
      description: "Medical report PDF has been downloaded successfully.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  // Direct PDF generation from external window recording data
  const generateDirectPDF = () => {
    const doc = new jsPDF();

    // Add title
    doc.setFontSize(20);
    doc.text("Medical Report Transcription", 20, 20);

    // Add patient info
    doc.setFontSize(14);
    doc.text(
      `Patient: ${selectedPatient?.[prompts[0][0]] || "Unknown"}`,
      20,
      35,
    );
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, 45);
    doc.text(`Duration: ${lastElapsedTime || 0} seconds`, 20, 55);

    // Add recording info
    doc.setFontSize(10);
    doc.text(`Recording Date: ${new Date().toLocaleDateString()}`, 20, 65);
    doc.text(`Source: External Audio Recorder`, 20, 75);

    // Add transcript section with sample data since we don't have actual transcript
    doc.setFontSize(16);
    doc.text("Transcript:", 20, 95);
    doc.setFontSize(10);
    let yPos = 105;

    // Add sample transcript since actual transcript data is not available
    const sampleTranscript = [
      "This is a recording from the external audio recorder.",
      "The patient was seen for a routine checkup.",
      "All vital signs were within normal limits.",
      "Patient reported feeling well with no complaints.",
    ];

    // Join as continuous text for consistency
    const transcriptText = sampleTranscript.join(" ");

    const lines = doc.splitTextToSize(transcriptText, 170);
    lines.forEach((splitLine) => {
      if (yPos > 280) {
        doc.addPage();
        yPos = 20;
      }
      doc.text(splitLine, 20, yPos);
      yPos += 6;
    });

    // Add facts section
    doc.addPage();
    doc.setFontSize(16);
    doc.text("Extracted Facts:", 20, 20);
    doc.setFontSize(10);
    yPos = 30;

    // Add sample facts since actual facts data is not available
    const sampleFacts = [
      { text: "Routine checkup completed", group: "General" },
      { text: "Vital signs within normal limits", group: "Medical" },
      { text: "Patient reports feeling well", group: "Subjective" },
      { text: "No complaints reported", group: "Assessment" },
    ];

    sampleFacts.forEach((fact, index) => {
      if (yPos > 280) {
        doc.addPage();
        yPos = 20;
      }
      const factText = `${index + 1}. ${fact.text} (Category: ${fact.group})`;
      const lines = doc.splitTextToSize(factText, 170);
      lines.forEach((line) => {
        if (yPos > 280) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(line, 20, yPos);
        yPos += 6;
      });
      yPos += 3; // Add space between facts
    });

    // Add SOAP Notes section
    doc.addPage();
    doc.setFontSize(16);
    doc.text("SOAP Notes:", 20, 20);
    doc.setFontSize(10);
    yPos = 30;

    // Add sample SOAP notes since actual SOAP notes data is not available
    const sampleSoapNotes = {
      transcript_only:
        "Subjective: Patient reports feeling well with no complaints. Objective: Vital signs within normal limits, physical examination unremarkable. Assessment: Patient is in good health. Plan: Continue current medications, follow up in 6 months.",
      facts_only:
        "Based on the recorded facts: Patient has completed routine checkup with normal vital signs. Assessment indicates good health status. Plan includes continued medications and routine follow-up.",
    };

    if (sampleSoapNotes.transcript_only) {
      doc.setFontSize(12);
      doc.text("Transcript-Based SOAP Notes:", 20, yPos);
      yPos += 10;
      doc.setFontSize(10);
      const soapLines = doc.splitTextToSize(
        sampleSoapNotes.transcript_only,
        170,
      );
      soapLines.forEach((line) => {
        if (yPos > 280) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(line, 20, yPos);
        yPos += 6;
      });
      yPos += 10;
    }

    if (sampleSoapNotes.facts_only) {
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }
      doc.setFontSize(12);
      doc.text("Facts-Based SOAP Notes:", 20, yPos);
      yPos += 10;
      doc.setFontSize(10);
      const soapLines = doc.splitTextToSize(sampleSoapNotes.facts_only, 170);
      soapLines.forEach((line) => {
        if (yPos > 280) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(line, 20, yPos);
        yPos += 6;
      });
    }

    doc.save("medical-report.pdf");

    // Show success message
    toast({
      title: "PDF Generated",
      description: "Medical report PDF has been downloaded successfully.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  // Update transcription results handlers
  const handleUpdateTranscript = (index, updatedData) => {
    const newTranscript = [...transcript];
    if (newTranscript[index]) {
      newTranscript[index] = { ...newTranscript[index], ...updatedData };
      setTranscript(newTranscript);
    }
  };

  const handleUpdateFacts = (index, updatedData) => {
    const newFacts = [...facts];
    if (newFacts[index]) {
      newFacts[index] = { ...newFacts[index], ...updatedData };
      setFacts(newFacts);
    }
  };

  const handleUpdateSoapNotes = (key, updatedData) => {
    const newSoapNotes = { ...soapNotes, [key]: updatedData };
    setSoapNotes(newSoapNotes);
  };

  const handleSaveTranscriptionResults = async (updatedData) => {
    try {
      // Update local state
      if (updatedData.transcript) setTranscript(updatedData.transcript);
      if (updatedData.facts) setFacts(updatedData.facts);
      if (updatedData.soapNotes) setSoapNotes(updatedData.soapNotes);

      // Update transcription results
      if (transcriptionResults) {
        const updatedResults = {
          ...transcriptionResults,
          transcript: updatedData.transcript || transcriptionResults.transcript,
          facts: updatedData.facts || transcriptionResults.facts,
          soap_notes: updatedData.soapNotes || transcriptionResults.soap_notes,
        };
        setTranscriptionResults(updatedResults);
      }

      // Save to database if needed
      if (selectedPatient?.id) {
        await cortiIntegrationService.saveTranscriptionResults(
          selectedPatient.id,
          transcriptionResults,
        );
      }

      toast({
        title: "Results Saved",
        description: "Transcription results have been saved successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error saving transcription results:", error);
      throw error;
    }
  };

  const patientId = refId || state?.id || state?.scheduleId;
  const patientName =
    state?.[prompts[0][0]] || state?.patient_name || "Unknown Patient";
  const ehrId = state?.EHR_ID || "";



  // ─── Task #269 (Leejoe 2026-06-30) PHI safety — HYBRID model ─────────────
  // When the parent's active patientId changes, tell the popup. The popup
  // decides what to do based on its OWN state:
  //   - Idle (not recording): auto-sync silently to the new patient. Same
  //     effect as if the user clicked "Sync Patient" — no friction.
  //   - Recording or paused: show a RED WARNING BANNER, do NOT close, do
  //     NOT stop the recording. User decides: finish + Stop & Save (which
  //     attributes to OLD patient correctly), then click Sync Patient
  //     manually — OR ignore and keep recording for OLD patient.
  //
  // Rationale (Leejoe correction 2026-06-30 late PM): the earlier
  // "force-close + stop+save" ship had two problems:
  //   1. Stop & Save then close means "no resume" — physician who wanted
  //      to resume after stepping away lost that option.
  //   2. Accidental Stop & Save destroys the pause capability.
  // Hybrid is the safety floor: it makes the mismatch impossible to miss
  // (red banner) without making destructive decisions for the user.
  //
  // Triggers only on patient SWITCH (prev → different). Initial mount
  // (prev was null) does NOT fire.
  const prevPatientIdForCloseRef = useRef(patientId);
  useEffect(() => {
    const prev = prevPatientIdForCloseRef.current;
    const curr = patientId;
    prevPatientIdForCloseRef.current = curr;
    if (!prev) return;          // initial mount or recovering from null
    if (prev === curr) return;  // same patient, no change
    console.log(
      "🎤 [AudioRecorder] patientId switched — notifying popup (hybrid). prev:",
      prev,
      "curr:",
      curr,
    );
    postMessageToExternalRecordingWindow({
      type: "PARENT_NAVIGATED_AWAY",
      oldPatientId: prev,
      newPatientId: curr || null,
      newPatientName: patientName || "",
      newEhrId: ehrId || "",
    });
  }, [patientId, patientName, ehrId, postMessageToExternalRecordingWindow]);

  useEffect(() => {
    const handleExternalPatientSyncRequest = (event) => {
      const message = event?.data;
      if (!message || message.type !== "REQUEST_PATIENT_SYNC") return;

      console.log("🎤 [AudioRecorder] Received REQUEST_PATIENT_SYNC from popup. Current parent context:", {
        patientId,
        patientName,
        ehrId,
      });

      if (!event.source || typeof event.source.postMessage !== "function") {
        console.warn("🎤 [AudioRecorder] Event source is not valid for posting messages back.");
        return;
      }

      try {
        event.source.postMessage({
          type: "PARENT_PATIENT_CONTEXT",
          patientId: patientId || "",
          patientName: patientName || "",
          ehrId: ehrId || "",
        }, "*");
        console.log("🎤 [AudioRecorder] Replied to REQUEST_PATIENT_SYNC with PARENT_PATIENT_CONTEXT.");
      } catch (err) {
        console.error("🎤 [AudioRecorder] Error posting PARENT_PATIENT_CONTEXT back to popup:", err);
      }
    };

    window.addEventListener("message", handleExternalPatientSyncRequest);
    return () => {
      window.removeEventListener("message", handleExternalPatientSyncRequest);
    };
  }, [patientId, patientName, ehrId]);

  if (!patientId) {
    return (
      <Box
        p={compactEmbed ? 2 : 4}
        w="100%"
        textAlign="center"
        display="flex"
        justifyContent="center"
        alignItems="center"
        minH={compactEmbed ? 0 : "200px"}
        flexDirection="column"
        gap={compactEmbed ? 2 : 4}
      >
        <Box color="red.500" fontSize={compactEmbed ? "sm" : "md"}>
          Missing patient context - cannot start recording
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box
        p={compactEmbed ? 0 : 4}
        w="100%"
        textAlign={compactEmbed ? "left" : "center"}
        display="flex"
        justifyContent={compactEmbed ? "stretch" : "center"}
        alignItems={compactEmbed ? "stretch" : "center"}
        minH={compactEmbed ? 0 : "200px"}
        flexDirection="column"
        gap={compactEmbed ? 2 : 4}
      >
        <Tooltip
          label="Read-only while viewing as another user"
          isDisabled={!isImpersonating()}
          hasArrow
        >
          <Button
            size={compactEmbed ? "md" : "lg"}
            w={compactEmbed ? "100%" : undefined}
            colorScheme={(() => {
              switch (recordingState) {
                case "recording":
                  return "red"; // Red when recording
                case "paused":
                  return "orange"; // Orange when paused
                case "opened":
                  return "blue"; // Blue when ready to start recording
                default:
                  return "blue"; // Default blue when closed
              }
            })()}
            onClick={handleMainButtonClick}
            isDisabled={isMinimized || isOpening || isImpersonating()}
            isLoading={isOpening}
            loadingText="Opening Recording Window..."
            leftIcon={
              recordingState === "recording" ? <FaMicrophone /> : undefined
            }
            variant="solid"
            borderWidth="1px"
          >
            {(() => {
              switch (recordingState) {
                case "recording":
                  return "Recording...";
                case "paused":
                  return "Paused";
                case "opened":
                  return "Start Recording";
                default:
                  return "Start Recording";
              }
            })()}
          </Button>
        </Tooltip>

        {/* Show what's being shared when system audio is enabled */}
        {sharedDisplayInfo && recordingState === "recording" && (
          <Box
            bg="blue.50"
            border="1px solid"
            borderColor="blue.200"
            borderRadius="md"
            p={3}
            maxW={compactEmbed ? "100%" : "400px"}
            w={compactEmbed ? "100%" : undefined}
            textAlign="center"
          >
            <Box fontSize="sm" color="blue.700">
              🎙️ System audio: Sharing "
              {sharedDisplayInfo.label ||
                sharedDisplayInfo.displaySurface ||
                "Unknown"}
              "
            </Box>
          </Box>
        )}

        {/* Transcription in Progress */}
        {isTranscribing && (
          <Box
            bg="yellow.50"
            border="1px solid"
            borderColor="yellow.200"
            borderRadius="md"
            p={4}
            maxW={compactEmbed ? "100%" : "400px"}
            w={compactEmbed ? "100%" : undefined}
            textAlign="center"
          >
            <Box fontSize="sm" color="yellow.700" mb={2}>
              ⏳ Transcribing audio...
            </Box>
            {transcriptionProgress && (
              <Box fontSize="xs" color="yellow.600">
                {transcriptionProgress.message}
              </Box>
            )}
          </Box>
        )}

        {/* Transcription Error */}
        {transcriptionError && (
          <Box
            bg="red.50"
            border="1px solid"
            borderColor="red.200"
            borderRadius="md"
            p={4}
            maxW={compactEmbed ? "100%" : "400px"}
            w={compactEmbed ? "100%" : undefined}
            textAlign="center"
          >
            <Box fontSize="sm" color="red.600" mb={3}>
              ❌ Transcription failed: {transcriptionError.error}
            </Box>
            <Button
              size="sm"
              colorScheme="red"
              variant="outline"
              onClick={handleTranscribeSavedAudio}
              isDisabled={isTranscribing}
            >
              Retry Transcription
            </Button>
          </Box>
        )}

        {popupBlocked && (
          <Box
            bg="red.50"
            border="1px solid"
            borderColor="red.200"
            borderRadius="md"
            p={4}
            maxW={compactEmbed ? "100%" : "400px"}
            w={compactEmbed ? "100%" : undefined}
            textAlign="center"
          >
            <Box fontSize="sm" color="red.600" mb={3}>
              Popup blocked! Please allow popups for this site and try again.
            </Box>
            <Button
              size="sm"
              colorScheme="red"
              variant="outline"
              onClick={retryOpenWindow}
            >
              Retry Opening Window
            </Button>
          </Box>
        )}
      </Box>

      <ExternalRecordingPage
        ref={setExternalWindowRef}
        state={state}
        refId={refId}
        onDataChange={onDataChange}
        session={session}
        popDeps={popDeps}
        setSession={setSession}
        override={override}
        setShowPersistentComponent={setShowPersistentComponent}
        condFunc={condFunc}
        autoSaveSetting={autoSaveSetting}
        isTranscriptionModalOpen={isTranscriptionModalOpen}
        setIsTranscriptionModalOpen={setIsTranscriptionModalOpen}
        transcriptionData={transcriptionData}
        setTranscriptionData={setTranscriptionData}
        onExternalClose={handleExternalClose}
        onPopupBlocked={handlePopupBlocked}
        patientId={patientId}
        patientName={patientName}
        ehrId={ehrId}
        supabaseUrl={supabaseUrl}
        bucketName={bucketName_files || "files"}
        tableName={tableName}
        prompts={prompts}
        onAudioSaved={handleAudioSaved}
        shouldOpenImmediately={isExternalWindowOpen}
        liveTranscription={liveTranscriptionDefault}
        onRecordingStateChange={handleRecordingStateChange}
        userRole={userRole}
      />
    </>
  );
};

export default AudioRecorder;
